import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth";
import KPICard from "../components/KPICard";
import { StatusBadge, PriorityBadge } from "../components/Badges";
import PositionFormDialog from "../components/PositionFormDialog";
import BulkErrorDialog from "../components/BulkErrorDialog";
import MultiSelectFilter from "../components/MultiSelectFilter";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Search, Plus, Upload, Download, MoreVertical, Eye, Pencil, Copy,
  Trash2, PauseCircle,
  Briefcase, UserCheck, FolderOpen, PauseOctagon, UserMinus, PlayCircle,
  LayoutGrid, Rows3, MapPin, User as UserIcon,
} from "lucide-react";

const STATUSES = ["Open", "Closed", "To Start", "Offer Declined", "On Hold", "Closed-Resigned", "Cancelled"];
const PRIORITIES = ["High", "Medium", "Low"];

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // ---- URL-persisted filter helpers ----
  const getArr = (key) => {
    const v = searchParams.get(key);
    return v ? v.split(",").filter(Boolean) : [];
  };
  const getStr = (key) => searchParams.get(key) || "";

  const [brand, setBrand] = useState(getStr("brand") || "All");
  const [stats, setStats] = useState(null);
  const [positions, setPositions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState(getStr("search"));
  const [statusFilter, setStatusFilter] = useState(getArr("status"));
  const [regionFilter, setRegionFilter] = useState(getArr("region"));
  const [trfRaisedFilter, setTrfRaisedFilter] = useState(getStr("trf_raised"));
  const [departments, setDepartments] = useState([]);
  const [entities, setEntities] = useState([]);
  const [regions, setRegions] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [departmentFilter, setDepartmentFilter] = useState(getArr("department"));
  const [priorityFilter, setPriorityFilter] = useState(getArr("priority"));
  const [cityFilter, setCityFilter] = useState(getArr("city"));
  const [hiringManagerFilter, setHiringManagerFilter] = useState(getArr("hiring_manager"));
  const [recruiterFilter, setRecruiterFilter] = useState(getArr("recruiter"));
  const [sortBy, setSortBy] = useState(getStr("sort_by") || "created_at");
  const [sortDir, setSortDir] = useState(parseInt(getStr("sort_dir") || "-1", 10));
  const page = 1;
  const pageSize = 2000;

  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [view, setView] = useState(getStr("view") || "table");
  const [kanbanItems, setKanbanItems] = useState([]);
  const [kanbanLoading, setKanbanLoading] = useState(false);

  const canEdit = user?.role === "Admin" || user?.role === "HR";
  const canSeeBudget = user?.role === "Admin" || user?.role === "Management";
  const canDelete = user?.role === "HR";

  // Sync filter state -> URL so back-navigation restores it
  useEffect(() => {
    const next = {};
    if (brand && brand !== "All") next.brand = brand;
    if (search) next.search = search;
    if (statusFilter.length) next.status = statusFilter.join(",");
    if (regionFilter.length) next.region = regionFilter.join(",");
    if (departmentFilter.length) next.department = departmentFilter.join(",");
    if (priorityFilter.length) next.priority = priorityFilter.join(",");
    if (cityFilter.length) next.city = cityFilter.join(",");
    if (hiringManagerFilter.length) next.hiring_manager = hiringManagerFilter.join(",");
    if (recruiterFilter.length) next.recruiter = recruiterFilter.join(",");
    if (trfRaisedFilter) next.trf_raised = trfRaisedFilter;
    if (sortBy !== "created_at") next.sort_by = sortBy;
    if (sortDir !== -1) next.sort_dir = String(sortDir);
    if (view !== "table") next.view = view;
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line
  }, [brand, search, statusFilter, regionFilter, departmentFilter, priorityFilter, cityFilter, hiringManagerFilter, recruiterFilter, trfRaisedFilter, sortBy, sortDir, view]);

  const cityOptions = useMemo(() => {
    const fromStats = Object.keys(stats?.city_breakdown || {}).filter((c) => c && c !== "?");
    const merged = new Set([...fromStats, ...cityFilter]);
    return Array.from(merged).sort((a, b) => a.localeCompare(b));
  }, [stats, cityFilter]);

  const hiringManagerOptions = useMemo(() => {
    const fromStats = Object.keys(stats?.hiring_manager_breakdown || {}).filter((c) => c && c !== "?");
    const merged = new Set([...fromStats, ...hiringManagerFilter]);
    return Array.from(merged).sort((a, b) => a.localeCompare(b));
  }, [stats, hiringManagerFilter]);

  const loadStats = async () => {
    const joined = (arr) => (arr && arr.length ? arr.join(",") : undefined);
    const r = await api.get("/positions/stats", {
      params: {
        brand,
        status: joined(statusFilter),
        region: joined(regionFilter),
        department: joined(departmentFilter),
        priority: joined(priorityFilter),
        city: joined(cityFilter),
        hiring_manager: joined(hiringManagerFilter),
        recruiter: joined(recruiterFilter),
        trf_raised: trfRaisedFilter || undefined,
        search: search || undefined,
      },
    });
    setStats(r.data);
  };

  const loadPositions = async () => {
    setLoading(true);
    try {
      const joined = (arr) => (arr && arr.length ? arr.join(",") : undefined);
      const r = await api.get("/positions", {
        params: {
          brand,
          status: joined(statusFilter),
          region: joined(regionFilter),
          department: joined(departmentFilter),
          priority: joined(priorityFilter),
          city: joined(cityFilter),
          hiring_manager: joined(hiringManagerFilter),
          recruiter: joined(recruiterFilter),
          search: search || undefined,
          trf_raised: trfRaisedFilter || undefined,
          page, page_size: pageSize, sort_by: sortBy, sort_dir: sortDir,
        },
      });
      setPositions(r.data.items);
      setTotal(r.data.total);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const params = { only_active: true };
    api.get("/masters/departments", { params }).then((r) => setDepartments(r.data));
    api.get("/masters/entities", { params }).then((r) => setEntities(r.data));
    api.get("/masters/regions", { params }).then((r) => setRegions(r.data));
    api.get("/masters/recruiters", { params }).then((r) => setRecruiters(r.data));
  }, []);

  useEffect(() => { loadStats(); /* eslint-disable-next-line */ }, [brand, statusFilter, regionFilter, departmentFilter, priorityFilter, cityFilter, hiringManagerFilter, recruiterFilter, trfRaisedFilter]);
  useEffect(() => { loadPositions(); /* eslint-disable-next-line */ }, [brand, statusFilter, regionFilter, departmentFilter, priorityFilter, cityFilter, hiringManagerFilter, recruiterFilter, trfRaisedFilter, sortBy, sortDir]);
  useEffect(() => {
    const t = setTimeout(() => { loadPositions(); loadStats(); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const refresh = () => { loadStats(); loadPositions(); if (view === "kanban") loadKanban(); };

  const loadKanban = async () => {
    setKanbanLoading(true);
    try {
      const joined = (arr) => (arr && arr.length ? arr.join(",") : undefined);
      const r = await api.get("/positions", {
        params: {
          brand,
          status: joined(statusFilter),
          region: joined(regionFilter),
          department: joined(departmentFilter),
          priority: joined(priorityFilter),
          city: joined(cityFilter),
          hiring_manager: joined(hiringManagerFilter),
          recruiter: joined(recruiterFilter),
          search: search || undefined,
          trf_raised: trfRaisedFilter || undefined,
          page: 1, page_size: 500, sort_by: "updated_at", sort_dir: -1,
        },
      });
      setKanbanItems(r.data.items);
    } finally { setKanbanLoading(false); }
  };

  useEffect(() => {
    if (view === "kanban") loadKanban();
    // eslint-disable-next-line
  }, [view, brand, statusFilter, regionFilter, departmentFilter, priorityFilter, cityFilter, hiringManagerFilter, recruiterFilter, trfRaisedFilter, search]);

  const onDelete = async (id) => {
    try {
      await api.delete(`/positions/${id}`);
      toast.success("Position deleted");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const onHold = async (id) => {
    try {
      await api.post(`/positions/${id}/hold`);
      toast.success("Marked On Hold — Hiring Manager notified");
      refresh();
    } catch (err) {
      toast.error("Failed to mark on hold");
    }
  };

  const onDuplicate = async (id) => {
    try {
      const r = await api.post(`/positions/${id}/duplicate`);
      toast.success("Position duplicated");
      refresh();
      nav(`/positions/${r.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to duplicate");
    }
  };

  const downloadTemplate = async () => {
    const r = await api.get("/positions/template/download", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = "Recruitment_Template.xlsx"; a.click();
    URL.revokeObjectURL(url);
  };

  const onBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBulkUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/positions/bulk-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBulkResult(r.data);
      setBulkOpen(true);
      const done = [
        r.data.created ? `created ${r.data.created}` : null,
        r.data.updated ? `updated ${r.data.updated}` : null,
      ].filter(Boolean).join(", ") || "no changes";
      if (r.data.rejected) {
        toast.error(`Upload rejected — ${r.data.errors.length} row(s) have errors`);
      } else if (r.data.errors?.length) {
        toast.warning(`Bulk upload: ${done}, ${r.data.errors.length} skipped`);
        refresh();
      } else {
        toast.success(`Bulk upload: ${done}`);
        refresh();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk upload failed");
    } finally {
      setBulkUploading(false);
      e.target.value = "";
    }
  };

  const exportExcel = async () => {
    const r = await api.get("/reports/positions/excel", { params: { brand }, responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = `Recruitment_${brand}.xlsx`; a.click();
    URL.revokeObjectURL(url);
  };

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === 1 ? -1 : 1);
    else { setSortBy(col); setSortDir(-1); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Recruitment Overview</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-1">
            Dashboard
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track all open and closed positions across United Group entities.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={exportExcel} data-testid="export-excel-btn">
            <Download className="w-4 h-4 mr-2" /> Export
          </Button>
          {canEdit && (
            <>
              <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
                Template
              </Button>
              <label className="cursor-pointer">
                <input type="file" accept=".xlsx,.xls" className="hidden" onChange={onBulkUpload} data-testid="bulk-upload-input" />
                <Button asChild variant="outline" disabled={bulkUploading}>
                  <span><Upload className="w-4 h-4 mr-2" /> {bulkUploading ? "Uploading…" : "Bulk Upload"}</span>
                </Button>
              </label>
              <Button onClick={() => { setEditing(null); setOpenForm(true); }} data-testid="add-position-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Position
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Brand tabs + view toggle */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Tabs value={brand} onValueChange={(v) => setBrand(v)}>
          <TabsList className="bg-muted border border-border h-11 p-1">
            <TabsTrigger
              key="All" value="All"
              className="px-4 text-sm data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-soft text-muted-foreground"
              data-testid="tab-all"
            >All</TabsTrigger>
            {entities.map((b) => (
              <TabsTrigger
                key={b.id} value={b.name}
                className="px-4 text-sm data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-soft text-muted-foreground"
                data-testid={`tab-${b.name.toLowerCase().replace(/\s|\//g, '-')}`}
              >
                {b.name}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="inline-flex border border-border rounded-md overflow-hidden bg-card" role="group">
          <button
            onClick={() => setView("table")}
            className={`px-3 py-2 text-sm flex items-center gap-1.5 transition-colors ${view === "table" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
            data-testid="view-table-btn"
          >
            <Rows3 className="w-4 h-4" /> Table
          </button>
          <button
            onClick={() => setView("kanban")}
            className={`px-3 py-2 text-sm flex items-center gap-1.5 transition-colors border-l border-border ${view === "kanban" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
            data-testid="view-kanban-btn"
          >
            <LayoutGrid className="w-4 h-4" /> Kanban
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KPICard testId="kpi-total" label="Total" value={stats?.total ?? 0} icon={Briefcase} accent="primary" />
        <KPICard testId="kpi-open" label="Open" value={stats?.open ?? 0} icon={FolderOpen} accent="blue" />
        <KPICard testId="kpi-closed" label="Closed" value={stats?.closed ?? 0} icon={UserCheck} accent="emerald" />
        <KPICard testId="kpi-onhold" label="On Hold" value={stats?.on_hold ?? 0} icon={PauseOctagon} accent="amber" />
        <KPICard testId="kpi-tostart" label="To Start" value={stats?.to_start ?? 0} icon={PlayCircle} accent="violet" />
        <KPICard testId="kpi-resigned" label="Closed-Resigned" value={stats?.closed_resigned ?? 0} icon={UserMinus} accent="slate" />
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-border bg-card p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search candidate, role, location, manager…"
            className="pl-9 h-10"
          />
        </div>
        <MultiSelectFilter
          label="Status"
          value={statusFilter}
          options={STATUSES}
          onChange={setStatusFilter}
          testId="filter-status"
          width="w-[160px]"
        />
        <Select value={trfRaisedFilter || "all"} onValueChange={(v) => setTrfRaisedFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[130px] h-10" data-testid="filter-trf-raised"><SelectValue placeholder="TRF Raised" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">TRF: All</SelectItem>
            <SelectItem value="Yes">TRF: Yes</SelectItem>
            <SelectItem value="No">TRF: No</SelectItem>
          </SelectContent>
        </Select>
        <MultiSelectFilter
          label="Region"
          value={regionFilter}
          options={regions.map((r) => r.name)}
          onChange={setRegionFilter}
          testId="filter-region"
          width="w-[150px]"
        />
        <MultiSelectFilter
          label="Department"
          value={departmentFilter}
          options={departments.map((d) => d.name)}
          onChange={setDepartmentFilter}
          testId="filter-department"
          width="w-[170px]"
        />
        <MultiSelectFilter
          label="Priority"
          value={priorityFilter}
          options={PRIORITIES}
          onChange={setPriorityFilter}
          testId="filter-priority"
          width="w-[150px]"
        />
        <MultiSelectFilter
          label="City"
          value={cityFilter}
          options={cityOptions}
          onChange={setCityFilter}
          testId="filter-city"
          width="w-[160px]"
        />
        <MultiSelectFilter
          label="Hiring Manager"
          value={hiringManagerFilter}
          options={hiringManagerOptions}
          onChange={setHiringManagerFilter}
          testId="filter-hiring-manager"
          width="w-[180px]"
        />
        <MultiSelectFilter
          label="Recruiter"
          value={recruiterFilter}
          options={recruiters.map((r) => r.name)}
          onChange={setRecruiterFilter}
          testId="filter-recruiter"
          width="w-[160px]"
        />
        {(statusFilter.length || regionFilter.length || departmentFilter.length || priorityFilter.length || cityFilter.length || hiringManagerFilter.length || recruiterFilter.length || search || trfRaisedFilter) ? (
          <Button
            variant="ghost"
            size="sm"
            data-testid="filter-clear-btn"
            onClick={() => {
              setStatusFilter([]); setRegionFilter([]); setDepartmentFilter([]);
              setPriorityFilter([]); setCityFilter([]); setHiringManagerFilter([]);
              setRecruiterFilter([]);
              setSearch(""); setTrfRaisedFilter("");
            }}
          >
            Clear
          </Button>
        ) : null}
      </div>

      {/* Table (desktop) / cards (mobile) */}
      {view === "table" && (
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="hidden md:block max-h-[28rem] overflow-y-auto overflow-x-auto scrollbar-thin">
          <table className="text-sm" style={{ minWidth: "1800px" }}>
            <thead className="sticky top-0 z-20 bg-muted/95 backdrop-blur">
              <tr className="bg-muted/50 text-left text-[11px] uppercase tracking-widest text-muted-foreground">
                {[
                  ["position_code", "ID"],
                  ["brand", "Entity"],
                  ["product_brand", "Brand"],
                  ["status", "Status"],
                  ["trf_raised", "TRF Raised"],
                  ["designation", "Position"],
                  ["city", "City"],
                  ["candidate_name", "Candidate"],
                  ["department", "Department"],
                  ["country", "Country"],
                  ["region", "Region"],
                  ["recruiter", "Recruiter"],
                  ["hiring_manager", "Hiring Mgr"],
                  ["priority", "Priority"],
                  ["budget_type", "Budget Type"],
                  ...(canSeeBudget ? [["approximate_budget", "Approx. Budget"]] : []),
                  ["employment_type", "Emp Type"],
                  ["age", "Age"],
                  ["experience", "Exp"],
                  ["qualification", "Qualification"],
                  ["gender", "Gender"],
                  ["notice_period", "Notice"],
                  ["number_of_positions", "#"],
                  ["source_channel", "Source"],
                  ["trf_date", "TRF Date"],
                  ["trf_hold_date", "TRF Hold"],
                  ["trf_reopen_date", "TRF Reopen"],
                  ["offer_date", "Offer"],
                  ["offer_stage", "Offer Stage"],
                  ["date_of_joining", "DOJ"],
                  ["interview_status", "Interview"],
                  ["created_at", "Created"],
                ].map(([k, l]) => (
                  <th key={k} className="px-3 py-3 cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort(k)}>
                    {l}{sortBy === k ? (sortDir === 1 ? " ↑" : " ↓") : ""}
                  </th>
                ))}
                <th className="px-3 py-3 text-right whitespace-nowrap sticky right-0 bg-muted min-w-[110px] shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.06)]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={32} className="p-8 text-center text-muted-foreground">Loading…</td></tr>
              )}
              {!loading && positions.length === 0 && (
                <tr><td colSpan={32} className="p-12 text-center text-muted-foreground">
                  No positions match your filters.
                </td></tr>
              )}
              {positions.map((p) => (
                <tr
                  key={p.id}
                  className="border-t border-border hover:bg-muted/40 transition-colors cursor-pointer"
                  onClick={() => nav(`/positions/${p.id}`)}
                  data-testid={`row-${p.id}`}
                >
                  <td className="px-3 py-3 whitespace-nowrap font-mono text-xs font-semibold text-primary" data-testid={`row-code-${p.id}`}>{p.position_code || "—"}</td>
                  <td className="px-3 py-3 whitespace-nowrap"><span className="text-xs font-medium text-muted-foreground">{p.brand}</span></td>
                  <td className="px-3 py-3 text-foreground/70 whitespace-nowrap">{p.product_brand || "—"}</td>
                  <td className="px-3 py-3 whitespace-nowrap"><StatusBadge status={p.status} /></td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${p.trf_raised === "Yes" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                      {p.trf_raised || (p.trf_date ? "Yes" : "No")}
                    </span>
                  </td>
                  <td className="px-3 py-3 font-medium text-foreground whitespace-nowrap">{p.designation}</td>
                  <td className="px-3 py-3 text-foreground/80 whitespace-nowrap">{p.city || p.location || "—"}</td>
                  <td className="px-3 py-3 text-foreground/80 whitespace-nowrap">{p.candidate_name || "—"}</td>
                  <td className="px-3 py-3 text-foreground/80 whitespace-nowrap">{p.department}</td>
                  <td className="px-3 py-3 text-foreground/70 whitespace-nowrap">{p.country || "—"}</td>
                  <td className="px-3 py-3 text-foreground/70 whitespace-nowrap">{p.region}</td>
                  <td className="px-3 py-3 text-foreground/80 whitespace-nowrap min-w-[140px]">{p.recruiter}</td>
                  <td className="px-3 py-3 text-foreground/80 whitespace-nowrap">{p.hiring_manager}</td>
                  <td className="px-3 py-3 whitespace-nowrap"><PriorityBadge priority={p.priority} /></td>
                  <td className="px-3 py-3 text-foreground/70 whitespace-nowrap">{p.budget_type}</td>
                  {canSeeBudget && (
                    <td className="px-3 py-3 text-foreground/70 whitespace-nowrap">{p.approximate_budget || "—"}</td>
                  )}
                  <td className="px-3 py-3 text-foreground/70 whitespace-nowrap">{p.employment_type}</td>
                  <td className="px-3 py-3 text-foreground/70 whitespace-nowrap">{p.age || "—"}</td>
                  <td className="px-3 py-3 text-foreground/70 whitespace-nowrap">{p.experience || "—"}</td>
                  <td className="px-3 py-3 text-foreground/70 whitespace-nowrap">{p.qualification || "—"}</td>
                  <td className="px-3 py-3 text-foreground/70 whitespace-nowrap">{p.gender || "—"}</td>
                  <td className="px-3 py-3 text-foreground/70 whitespace-nowrap">{p.notice_period || "—"}</td>
                  <td className="px-3 py-3 text-foreground/70 whitespace-nowrap tabular-nums">{p.number_of_positions}</td>
                  <td className="px-3 py-3 text-foreground/70 whitespace-nowrap">{p.source_channel || "—"}</td>
                  <td className="px-3 py-3 text-muted-foreground text-xs whitespace-nowrap">{p.trf_date ? new Date(p.trf_date).toLocaleDateString() : "—"}</td>
                  <td className="px-3 py-3 text-muted-foreground text-xs whitespace-nowrap">{p.trf_hold_date ? new Date(p.trf_hold_date).toLocaleDateString() : "—"}</td>
                  <td className="px-3 py-3 text-muted-foreground text-xs whitespace-nowrap">{p.trf_reopen_date ? new Date(p.trf_reopen_date).toLocaleDateString() : "—"}</td>
                  <td className="px-3 py-3 text-muted-foreground text-xs whitespace-nowrap">{p.offer_date ? new Date(p.offer_date).toLocaleDateString() : "—"}</td>
                  <td className="px-3 py-3 text-foreground/70 whitespace-nowrap">{p.offer_stage || "—"}</td>
                  <td className="px-3 py-3 text-muted-foreground text-xs whitespace-nowrap">{p.date_of_joining ? new Date(p.date_of_joining).toLocaleDateString() : "—"}</td>
                  <td className="px-3 py-3 text-foreground/70 whitespace-nowrap">{p.interview_status || "—"}</td>
                  <td className="px-3 py-3 text-muted-foreground text-xs whitespace-nowrap">
                    {new Date(p.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-3 py-3 text-right whitespace-nowrap sticky right-0 bg-card min-w-[110px] shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.06)]" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="p-1.5 rounded-md hover:bg-muted" data-testid={`row-actions-${p.id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => nav(`/positions/${p.id}`)}>
                          <Eye className="w-4 h-4 mr-2" /> View
                        </DropdownMenuItem>
                        {canEdit && (
                          <>
                            <DropdownMenuItem onClick={() => { setEditing(p); setOpenForm(true); }}>
                              <Pencil className="w-4 h-4 mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onDuplicate(p.id)} data-testid={`row-duplicate-${p.id}`}>
                              <Copy className="w-4 h-4 mr-2" /> Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onHold(p.id)}>
                              <PauseCircle className="w-4 h-4 mr-2" /> Mark On Hold
                            </DropdownMenuItem>
                          </>
                        )}
                        {canDelete && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-destructive">
                                <Trash2 className="w-4 h-4 mr-2" /> Delete
                              </DropdownMenuItem>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete this position?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This permanently removes the position and all its attachments and comments.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => onDelete(p.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden divide-y divide-border">
          {loading && <div className="p-8 text-center text-muted-foreground">Loading…</div>}
          {!loading && positions.length === 0 && (
            <div className="p-12 text-center text-muted-foreground">No positions found.</div>
          )}
          {positions.map((p) => (
            <div key={p.id} className="p-4 hover:bg-muted/40 cursor-pointer" onClick={() => nav(`/positions/${p.id}`)}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-medium"><span className="font-mono text-xs text-primary mr-2">{p.position_code}</span>{p.designation}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{p.brand} · {p.city || p.location || "—"}</div>
                </div>
                <StatusBadge status={p.status} />
              </div>
              <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                <span>{p.recruiter}</span>
                <span>·</span>
                <PriorityBadge priority={p.priority} />
              </div>
            </div>
          ))}
        </div>

        {/* Total count footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/20 text-sm">
          <span className="text-muted-foreground tabular-nums">
            Showing <span className="font-semibold text-foreground">{total}</span> position{total === 1 ? "" : "s"}
          </span>
        </div>
      </div>
      )}

      {/* Kanban view */}
      {view === "kanban" && (
        <KanbanBoard
          items={kanbanItems}
          loading={kanbanLoading}
          onCardClick={(id) => nav(`/positions/${id}`)}
        />
      )}

      <PositionFormDialog
        open={openForm}
        onOpenChange={setOpenForm}
        position={editing}
        onSaved={() => { refresh(); setOpenForm(false); }}
      />
      <BulkErrorDialog open={bulkOpen} onOpenChange={setBulkOpen} result={bulkResult} />
    </div>
  );
}


const KANBAN_COLUMNS = [
  { key: "Open", color: "bg-blue-50 text-blue-700 border-blue-200" },
  { key: "To Start", color: "bg-violet-50 text-violet-700 border-violet-200" },
  { key: "On Hold", color: "bg-amber-50 text-amber-700 border-amber-200" },
  { key: "Closed", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { key: "Closed-Resigned", color: "bg-slate-100 text-slate-700 border-slate-200" },
  { key: "Offer Declined", color: "bg-red-50 text-red-700 border-red-200" },
  { key: "Cancelled", color: "bg-rose-50 text-rose-700 border-rose-200" },
];

function KanbanBoard({ items, loading, onCardClick }) {
  if (loading) return <div className="text-center text-muted-foreground py-12">Loading…</div>;
  return (
    <div className="overflow-x-auto scrollbar-thin pb-2" data-testid="kanban-board">
      <div className="flex gap-4 min-w-max">
        {KANBAN_COLUMNS.map((col) => {
          const cards = items.filter((p) => p.status === col.key);
          return (
            <div
              key={col.key}
              className="w-72 shrink-0 rounded-xl border border-border bg-muted/30"
              data-testid={`kanban-col-${col.key.toLowerCase().replace(/\s/g, "-")}`}
            >
              <div className={`flex items-center justify-between px-3 py-2.5 border-b border-border rounded-t-xl ${col.color}`}>
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
                  <span className="text-xs font-semibold uppercase tracking-widest">{col.key}</span>
                </div>
                <span className="text-xs font-semibold tabular-nums opacity-80">{cards.length}</span>
              </div>
              <div className="p-2 space-y-2 max-h-[68vh] overflow-y-auto scrollbar-thin">
                {cards.length === 0 && (
                  <div className="text-center text-xs text-muted-foreground py-6 italic">No positions</div>
                )}
                {cards.map((p) => (
                  <div
                    key={p.id}
                    onClick={() => onCardClick(p.id)}
                    className="rounded-lg border border-border bg-card p-3 shadow-soft hover:shadow-card hover:border-primary/40 transition-all cursor-pointer"
                    data-testid={`kanban-card-${p.id}`}
                  >
                    <div className="font-mono text-[10px] font-semibold text-primary mb-1" data-testid={`kanban-code-${p.id}`}>{p.position_code}</div>
                    <div className="font-medium text-sm leading-tight">{p.designation}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {p.candidate_name || <span className="italic">No candidate yet</span>}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
                      <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-widest">
                        {p.brand}
                      </span>
                      <PriorityBadge priority={p.priority} />
                    </div>
                    <div className="flex items-center gap-2 mt-2 text-[11px] text-muted-foreground">
                      <span className="flex items-center gap-1 truncate"><MapPin className="w-3 h-3 shrink-0" />{p.city || p.location || "—"}</span>
                      <span>·</span>
                      <span className="flex items-center gap-1 truncate"><UserIcon className="w-3 h-3 shrink-0" />{p.recruiter}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
